/**
 * MODULE: domain/pages/frontend/components/PagesEditorLayoutContext.tsx
 * PURPOSE: Shared page width mode (standard vs full) for note and database pages
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  pageEditorSectionPaddingClass,
  pageEditorShellClass,
  pageEditorWidthModeLabel,
  readPageEditorWidthMode,
  togglePageEditorWidthMode,
  writePageEditorWidthMode,
  type PageEditorWidthMode,
} from '../utils/pageEditorWidthUtils';

type PagesEditorLayoutContextValue = {
  widthMode: PageEditorWidthMode;
  toggleWidthMode: () => void;
  shellClass: string;
  sectionPaddingClass: string;
  widthModeLabel: string;
};

const PagesEditorLayoutContext = createContext<PagesEditorLayoutContextValue | null>(null);

export function usePagesEditorLayout(): PagesEditorLayoutContextValue {
  const ctx = useContext(PagesEditorLayoutContext);
  if (!ctx) {
    throw new Error('usePagesEditorLayout must be used within PagesEditorLayoutProvider');
  }
  return ctx;
}

type ProviderProps = {
  workspaceId: string;
  children: ReactNode;
};

export function PagesEditorLayoutProvider({ workspaceId, children }: ProviderProps) {
  const [widthMode, setWidthMode] = useState<PageEditorWidthMode>(() =>
    readPageEditorWidthMode(workspaceId)
  );

  useEffect(() => {
    setWidthMode(readPageEditorWidthMode(workspaceId));
  }, [workspaceId]);

  const toggleWidthMode = useCallback(() => {
    setWidthMode((current) => {
      const next = togglePageEditorWidthMode(current);
      writePageEditorWidthMode(workspaceId, next);
      return next;
    });
  }, [workspaceId]);

  const value = useMemo(
    () => ({
      widthMode,
      toggleWidthMode,
      shellClass: pageEditorShellClass(widthMode),
      sectionPaddingClass: pageEditorSectionPaddingClass(widthMode),
      widthModeLabel: pageEditorWidthModeLabel(widthMode),
    }),
    [toggleWidthMode, widthMode]
  );

  return (
    <PagesEditorLayoutContext.Provider value={value}>{children}</PagesEditorLayoutContext.Provider>
  );
}
